// useAuthHelpers.js
import { Header } from '@/@vxapp/@Enums/Header'
import { Cookie } from '@/@vxapp/@Enums/Cookie'
import { HeaderNames } from '@/@vxapp/@Enums/PHP/HTTP/HeaderNames'
import { jwtDecode } from 'jwt-decode'

function allStatusesInactive(states) {
  return Object.values(states).every(val => val === false || val === undefined)
}

export function sanitizePermissions(permissions) {
  if (!Array.isArray(permissions)) return null

  return permissions.filter(permission => {
    if (!permission || typeof permission !== 'object') return false
    if (permission.inverted === true) return true
    const subjects = Array.isArray(permission.subject) ? permission.subject : [permission.subject]

    return (
      permission.permissionGUID !== '42069' &&
      permission.permissionGUID !== 42069 &&
      !subjects.some(subject => subject === 'all' || subject === '*')
    )
  })
}

function loadPermissionsFromStorage() {
  try {
    const raw = localStorage.getItem(Cookie.Permissions)
    const parsed = raw ? JSON.parse(raw) : null

    const permissions = sanitizePermissions(parsed)
    if (permissions && permissions.length !== parsed.length)
      localStorage.setItem(Cookie.Permissions, JSON.stringify(permissions))

    return permissions
  } catch (err) {
    console.error(
      '[AuthHelpers][loadPermissionsFromStorage] Failed to parse permissions from storage',
      err
    )

    return null
  }
}

function savePermissionsToStorage(permissions) {
  const explicitPermissions = sanitizePermissions(permissions)
  if (explicitPermissions) {
    try {
      localStorage.setItem(Cookie.Permissions, JSON.stringify(explicitPermissions))
      console.debug('[Auth][savePermissionsToStorage] Permissions Saved')
    } catch (err) {
      console.error(
        '[Auth][savePermissionsToStorage] Failed to stringify permissions for storage',
        err
      )
    }
  } else {
    console.warn('[Auth][savePermissionsToStorage] invalid permissions, skipping')
  }
}

function isCookieOld(cookieRef, minutes = 5) {
  const MINUTES_MS = minutes * 60 * 1000

  const raw = cookieRef.value
  if (!raw) return true

  // treat as expired if not set
  if (typeof raw !== 'object' || typeof raw.timestamp !== 'number') {
    return true
  }

  return Date.now() - raw.timestamp >= MINUTES_MS
}

// for guest authentication
function isHeaderApiHeaderSet(headers) {
  const expected = HeaderNames.API_GUID.toLowerCase()

  return Object.keys(headers).some(key => key.toLowerCase() === expected)
}

// for guest authentication
function formatResponseHeaders(headers) {
  const expected = HeaderNames.API_GUID
  const lookupKey = expected.toLowerCase()
  const actualKey = Object.keys(headers).find(key => key.toLowerCase() === lookupKey)
  if (!actualKey) {
    console.warn('[AuthHelpers][formatResponseHeaders] Headers are invalid', headers)

    return null
  }

  console.debug('[AuthHelpers][formatResponseHeaders] Headers are valid', headers)

  return { [expected]: headers[actualKey] }
}

function validateAbility(incoming, outgoing) {
  const incomingRules = incoming?.rules || []
  const outgoingRules = outgoing?.rules || []
  const isIdentical = JSON.stringify(incomingRules) === JSON.stringify(outgoingRules)
  if (isIdentical) {
    return false
  }

  return true
}

function isRenewalAllowed(jwt) {
  let token = jwtDecoder(jwt)

  return token?.data?.isRenewAllowed ?? false
}

function getTokenExpiry(jwt) {
  return jwtDecoder(jwt)?.exp ?? 0
}

function isTokenExpired(jwt) {
  let token = jwtDecoder(jwt)
  const exp = token?.exp ?? null
  const now = Date.now() / 1000

  const hasExpired = exp && exp <= now

  if (hasExpired) {
    console.warn(
      `[AuthHelpers] Token is ${hasExpired ? 'expired' : 'valid expires'}; 
      at ${new Date(exp * 1000).toISOString()}`
    )
  } else {
    console.debug(
      `[AuthHelpers] Token is ${hasExpired ? 'expired' : 'valid expires'}; 
      at ${new Date(exp * 1000).toISOString()}`
    )
  }

  return hasExpired
}

function getAuthHeader(hdrs) {
  if (!hdrs || typeof hdrs !== 'object') {
    //console.warn('[AuthHelpers][getAuthHeader] No headers sent')
    return null
  }

  return getHeader(Header.Auth, hdrs)
}

function getApiHeader(hdrs) {
  if (!hdrs || typeof hdrs !== 'object') {
    //console.warn('[AuthHelpers][getApiHeader] No headers sent')
    return null
  }

  const expected = HeaderNames.API_GUID.toLowerCase()
  const actualKey = Object.keys(hdrs).find(key => key.toLowerCase() === expected)
  if (!actualKey) {
    return null
  }

  return getHeader(actualKey, hdrs)
}

function getRoleGUID(hdrs) {
  if (!hdrs || typeof hdrs !== 'object') {
    console.warn('[AuthHelpers][getRoleGUID] No headers sent')

    return null
  }

  const tokenData = getTokenData(hdrs)
  if (!tokenData) {
    return null
  }

  const roleGUID = tokenData?.data?.roleGUID ?? null
  if (!roleGUID) {
    console.warn('[AuthHelpers][getRoleGUID] No roleGUID found token data', tokenData)

    return null
  }

  return roleGUID
}

function getUserData(hdrs) {
  if (!hdrs || typeof hdrs !== 'object') {
    console.warn('[AuthHelpers][getUserData] No headers sent')

    return null
  }

  const tokenData = getTokenData(hdrs)
  if (!tokenData) {
    return null
  }

  const userData = tokenData?.data?.userData ?? null
  if (!userData) {
    console.warn('[AuthHelpers][getUserData] No userData found token data', tokenData)

    return null
  }

  return userData
}

function getUserPreferences() {
  return {
    displayDateFormat: 'd-m-Y',
    displayTimeFormat: 'H:i',
    displayDateTimeFormat: 'd-m-Y H:i',
  }
}

/* ******** helper helpers ******** */
function getHeader(headerToGet, allHeaders) {
  const header = allHeaders[headerToGet]
  if (typeof header !== 'string') {
    //console.warn(`[AuthHelpers] ${headerToGet} is not a string`, allHeaders)
    return null
  }

  return header
}

function getTokenData(hdrs) {
  const jwt = getAuthHeader(hdrs)
  if (!jwt) {
    return null
  }

  const tokenData = jwtDecoder(jwt)
  if (!tokenData) {
    //console.warn('[AuthHelpers][getTokenData] Token Decode Failed')
    return null
  }

  return tokenData
}

function jwtDecoder(token) {
  if (typeof token !== 'string') {
    console.error('[AuthHelpers] Token was not a string', token)

    return null
  }

  try {
    return jwtDecode(token)
  } catch (err) {
    void err
    console.error('[AuthHelpers] Invalid JWT')

    return null
  }
}

/* ** */

export function useAuthHelpers() {
  return {
    getAuthHeader,
    getApiHeader,
    getRoleGUID,
    getUserData,
    getUserPreferences,
    validateAbility,
    sanitizePermissions,
    isTokenExpired,
    getTokenExpiry,
    isRenewalAllowed,
    isHeaderApiHeaderSet,
    formatResponseHeaders,
    isCookieOld,
    loadPermissionsFromStorage,
    savePermissionsToStorage,
    allStatusesInactive,
  }
}
